<app-order-entity-filters [filters]="filters" (filtersChange)="onFiltersChange($event)">
  <button
    class="btn btn-soft-primary btn-sm d-flex align-items-center gap-2"
    (click)="exportPending()"
    [disabled]="isExporting">
    @if (isExporting) {
      <span class="spinner-border spinner-border-sm"></span>
    } @else {
      <i class="bi bi-download"></i>
    }
    Export
  </button>
</app-order-entity-filters>

@if (isDataLoading) {
  <app-loading-spinner [message]="'Loading pending orders...'" />
} @else {
  @if (orders.length === 0) {
    <div class="text-center py-5">
      <div class="bg-light d-inline-block p-4 rounded-circle mb-3">
        <i class="bi bi-hourglass-split text-muted fs-1"></i>
      </div>
      @if (hasActiveFilters) {
        <h5 class="text-dark fw-bold">No Matching Pending Orders</h5>
        <p class="text-muted">
          No pending orders match all of the active filters. Try removing one of the filters above.
        </p>
      } @else {
        <h5 class="text-dark fw-bold">No Pending Orders</h5>
        <p class="text-muted">There are no pending or partially shipped orders at the moment.</p>
      }
    </div>
  } @else {
    <div class="table-responsive">
      <table class="table table-hover align-middle mb-0">
        <thead class="bg-light">
          <tr>
            <th class="ps-4 border-0 text-uppercase small fw-bold text-muted py-3">Order Number</th>
            <th class="border-0 text-uppercase small fw-bold text-muted py-3">Order Date</th>
            <th class="border-0 text-uppercase small fw-bold text-muted py-3">Dealer</th>
            <th class="border-0 text-uppercase small fw-bold text-muted py-3">Products</th>
            <th class="border-0 text-uppercase small fw-bold text-muted py-3">Shipped Boxes</th>
            <th class="border-0 text-uppercase small fw-bold text-muted py-3">Pending Boxes</th>
            <th class="border-0 text-uppercase small fw-bold text-muted py-3">Status</th>
          </tr>
        </thead>
        <tbody>
          @for (order of orders; track order.id) {
            <tr class="border-bottom" style="cursor: pointer" (click)="viewOrder(order.id)">
              <td class="ps-4 py-3 fw-bold text-dark">#{{ order.order_number }}</td>
              <td class="py-3 text-muted small">{{ order.created_at | date: 'dd MMM yyyy' }}</td>
              <td class="py-3">
                <div class="fw-semibold small">{{ order.dealer.shop_name }}</div>
                <small class="text-muted">{{ order.dealer.first_name }} {{ order.dealer.last_name }}</small>
              </td>
              <td class="py-3">
                <div class="d-flex flex-column gap-2">
                  @for (item of order.items; track item.id) {
                    <div class="d-flex justify-content-between align-items-start gap-3">
                      <div>
                        <div class="small fw-semibold">{{ getProductName(item) }}</div>
                        <small class="text-muted">{{ getPackagingLabel(item) }}</small>
                      </div>
                      <span class="small fw-semibold text-nowrap">
                        {{ item.box_quantity }} {{ item.box_quantity === 1 ? 'box' : 'boxes' }}
                      </span>
                    </div>
                  }

                  <div class="d-flex justify-content-between align-items-center gap-3 border-top pt-2">
                    <span class="small fw-semibold text-muted">Total</span>
                    <span class="small fw-bold text-nowrap">
                      {{ order.total_boxes_ordered }} {{ order.total_boxes_ordered === 1 ? 'box' : 'boxes' }}
                    </span>
                  </div>
                </div>
              </td>
              <td class="py-3">
                <!-- One line per product, so an order does not hide behind a single number. -->
                <div class="d-flex flex-column gap-2">
                  @for (item of order.items; track item.id) {
                    <div class="d-flex justify-content-between align-items-start gap-3">
                      <div>
                        <div class="small fw-semibold">{{ getProductName(item) }}</div>
                        <small class="text-muted">{{ getPackagingLabel(item) }}</small>
                      </div>
                      <span class="small fw-semibold text-nowrap">
                        {{ item.shipped_quantity }} {{ item.shipped_quantity === 1 ? 'box' : 'boxes' }}
                      </span>
                    </div>
                  }

                  <div class="d-flex justify-content-between align-items-center gap-3 border-top pt-2">
                    <span class="small fw-semibold text-muted">Total</span>
                    <span class="small fw-bold text-nowrap">
                      {{ order.total_boxes_shipped }} {{ order.total_boxes_shipped === 1 ? 'box' : 'boxes' }}
                    </span>
                  </div>
                </div>
              </td>
              <td class="py-3">
                <!-- One line per product, so an order does not hide behind a single number. -->
                <div class="d-flex flex-column gap-2">
                  @for (item of order.items; track item.id) {
                    <div class="d-flex justify-content-between align-items-start gap-3 text-danger">
                      <div>
                        <div class="small fw-bold">{{ getProductName(item) }}</div>
                        <small class="fw-bold">{{ getPackagingLabel(item) }}</small>
                      </div>
                      <span class="small fw-bold text-nowrap">
                        {{ item.pending_quantity }} {{ item.pending_quantity === 1 ? 'box' : 'boxes' }}
                      </span>
                    </div>
                  }

                  <div class="d-flex justify-content-between align-items-center gap-3 border-top pt-2 text-danger">
                    <span class="small fw-bold">Total</span>
                    <span class="small fw-bold text-danger text-nowrap">
                      {{ order.total_boxes_ordered - order.total_boxes_shipped }}
                      {{ order.total_boxes_ordered - order.total_boxes_shipped === 1 ? 'box' : 'boxes' }}
                    </span>
                  </div>
                </div>
              </td>
              <td class="py-3">
                @if (order.is_partially_shipped && !order.is_fully_shipped) {
                  <span class="app-badge badge-warning">Partially Shipped</span>
                } @else {
                  <span class="app-badge badge-secondary">Pending</span>
                }
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>

    <div class="mt-4">
      <app-pagination
        [totalCount]="totalCount"
        [hasNextPage]="hasNextPage"
        [hasPreviousPage]="hasPreviousPage"
        [currentPage]="currentPage"
        [pageSize]="pageSize"
        (pageChange)="onPageChange($event)" />
    </div>
  }
}
